import React, { useState, useEffect } from 'react';
import { galleryAPI } from '../services/api';

const AdminDashboard = ({ token, admin, onLogout }) => {
  const [galleries, setGalleries] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showUploadForm, setShowUploadForm] = useState(false);
  const [uploading, setUploading] = useState(false);

  const [formData, setFormData] = useState({
    service: 'Jardinage',
    title: '',
    description: '',
    location: '',
    duration: '',
    price: '',
    beforeImage: null,
    afterImage: null
  });

  // Charger les photos au démarrage
  useEffect(() => {
    loadGalleries();
  }, []);

  const loadGalleries = async () => {
    try {
      const response = await galleryAPI.getAll();
      if (response.success) {
        setGalleries(response.data);
      }
    } catch (error) {
      console.error('Erreur chargement galerie:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleInputChange = (e) => {
    setFormData({
      ...formData,
      [e.target.name]: e.target.value
    });
  };

  const handleFileChange = (e) => {
    setFormData({
      ...formData,
      [e.target.name]: e.target.files[0]
    });
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setUploading(true);

    try {
      // Créer FormData pour l'upload
      const data = new FormData();
      data.append('service', formData.service);
      data.append('title', formData.title);
      data.append('description', formData.description);
      data.append('location', formData.location);
      data.append('duration', formData.duration);
      data.append('price', formData.price);
      data.append('beforeImage', formData.beforeImage);
      data.append('afterImage', formData.afterImage);

      const response = await galleryAPI.create(data, token);

      if (response.success) {
        alert('Photo ajoutée avec succès !');
        setShowUploadForm(false);
        setFormData({
          service: 'Jardinage',
          title: '',
          description: '',
          location: '',
          duration: '',
          price: '',
          beforeImage: null,
          afterImage: null
        });
        loadGalleries(); // Recharger la liste
      }
    } catch (error) {
      alert('Erreur lors de l\'ajout : ' + (error.response?.data?.message || error.message));
    } finally {
      setUploading(false);
    }
  };

  const handleDelete = async (id) => {
    if (!window.confirm('Êtes-vous sûr de vouloir supprimer cette photo ?')) {
      return;
    }

    try {
      const response = await galleryAPI.delete(id, token);
      if (response.success) {
        alert('Photo supprimée !');
        loadGalleries();
      }
    } catch (error) {
      alert('Erreur lors de la suppression');
    }
  };

  const API_URL = process.env.REACT_APP_API_URL || 'http://localhost:5000/api';
  const BASE_URL = API_URL.replace('/api', '');

  return (
    <div className="min-h-screen bg-gray-100">
      {/* Header */}
      <div className="bg-gradient-to-r from-orange-500 to-yellow-400 text-white p-6 shadow-lg">
        <div className="max-w-7xl mx-auto flex justify-between items-center">
          <div>
            <h1 className="text-3xl font-bold">Panneau d'Administration</h1>
            <p className="text-white/90">Bienvenue, {admin.username}</p>
          </div>
          <button
            onClick={onLogout}
            className="bg-white/20 hover:bg-white/30 px-6 py-2 rounded-full font-semibold transition-colors"
          >
            Déconnexion
          </button>
        </div>
      </div>

      <div className="max-w-7xl mx-auto p-6">
        {/* Bouton Ajouter */}
        <div className="mb-6">
          <button
            onClick={() => setShowUploadForm(!showUploadForm)}
            className="bg-gradient-to-r from-orange-500 to-yellow-400 text-white px-8 py-4 rounded-xl font-bold text-lg hover:shadow-lg transition-all"
          >
            {showUploadForm ? '❌ Annuler' : '➕ Ajouter une photo'}
          </button>
        </div>

        {/* Formulaire d'upload */}
        {showUploadForm && (
          <div className="bg-white rounded-2xl p-8 shadow-xl mb-8">
            <h2 className="text-2xl font-bold mb-6">Nouvelle Réalisation</h2>
            <form onSubmit={handleSubmit} className="space-y-6">
              <div className="grid md:grid-cols-2 gap-6">
                <div>
                  <label className="block text-gray-700 font-semibold mb-2">Service *</label>
                  <select
                    name="service"
                    value={formData.service}
                    onChange={handleInputChange}
                    required
                    className="w-full px-4 py-3 rounded-xl border-2 border-gray-200 focus:border-orange-500 focus:outline-none"
                  >
                    <option>Jardinage</option>
                    <option>Bricolage</option>
                    <option>Déménagement</option>
                    <option>Nettoyage</option>
                  </select>
                </div>

                <div>
                  <label className="block text-gray-700 font-semibold mb-2">Titre *</label>
                  <input
                    type="text"
                    name="title"
                    value={formData.title}
                    onChange={handleInputChange}
                    required
                    className="w-full px-4 py-3 rounded-xl border-2 border-gray-200 focus:border-orange-500 focus:outline-none"
                    placeholder="Ex: Tonte de pelouse - Maison Dupont"
                  />
                </div>

                <div>
                  <label className="block text-gray-700 font-semibold mb-2">Lieu</label>
                  <input
                    type="text"
                    name="location"
                    value={formData.location}
                    onChange={handleInputChange}
                    className="w-full px-4 py-3 rounded-xl border-2 border-gray-200 focus:border-orange-500 focus:outline-none"
                    placeholder="Paris"
                  />
                </div>

                <div>
                  <label className="block text-gray-700 font-semibold mb-2">Durée</label>
                  <input
                    type="text"
                    name="duration"
                    value={formData.duration}
                    onChange={handleInputChange}
                    className="w-full px-4 py-3 rounded-xl border-2 border-gray-200 focus:border-orange-500 focus:outline-none"
                    placeholder="2 heures"
                  />
                </div>

                <div>
                  <label className="block text-gray-700 font-semibold mb-2">Prix</label>
                  <input
                    type="text"
                    name="price"
                    value={formData.price}
                    onChange={handleInputChange}
                    className="w-full px-4 py-3 rounded-xl border-2 border-gray-200 focus:border-orange-500 focus:outline-none"
                    placeholder="80€"
                  />
                </div>
              </div>

              <div>
                <label className="block text-gray-700 font-semibold mb-2">Description</label>
                <textarea
                  name="description"
                  value={formData.description}
                  onChange={handleInputChange}
                  rows="3"
                  className="w-full px-4 py-3 rounded-xl border-2 border-gray-200 focus:border-orange-500 focus:outline-none"
                  placeholder="Détails de l'intervention..."
                ></textarea>
              </div>

              <div className="grid md:grid-cols-2 gap-6">
                <div>
                  <label className="block text-gray-700 font-semibold mb-2">Photo AVANT *</label>
                  <input
                    type="file"
                    name="beforeImage"
                    onChange={handleFileChange}
                    required
                    accept="image/*"
                    className="w-full px-4 py-3 rounded-xl border-2 border-gray-200 focus:border-orange-500 focus:outline-none"
                  />
                </div>

                <div>
                  <label className="block text-gray-700 font-semibold mb-2">Photo APRÈS *</label>
                  <input
                    type="file"
                    name="afterImage"
                    onChange={handleFileChange}
                    required
                    accept="image/*"
                    className="w-full px-4 py-3 rounded-xl border-2 border-gray-200 focus:border-orange-500 focus:outline-none"
                  />
                </div>
              </div>

              <button
                type="submit"
                disabled={uploading}
                className="w-full bg-gradient-to-r from-orange-500 to-yellow-400 text-white py-4 rounded-xl font-bold text-lg hover:shadow-lg transition-all disabled:opacity-50"
              >
                {uploading ? 'Upload en cours...' : '✅ Ajouter'}
              </button>
            </form>
          </div>
        )}

        {/* Liste des photos */}
        <div className="bg-white rounded-2xl p-8 shadow-xl">
          <h2 className="text-2xl font-bold mb-6">Galerie ({galleries.length} photos)</h2>

          {loading ? (
            <p className="text-center text-gray-500 py-8">Chargement...</p>
          ) : galleries.length === 0 ? (
            <p className="text-center text-gray-500 py-8">Aucune photo pour le moment</p>
          ) : (
            <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-6">
              {galleries.map((item) => (
                <div key={item._id} className="border-2 border-gray-200 rounded-xl overflow-hidden hover:border-orange-500 transition-colors">
                  <div className="grid grid-cols-2 gap-1">
                    <img
                      src={BASE_URL + item.beforeImage}
                      alt="Avant"
                      className="w-full h-40 object-cover"
                    />
                    <img
                      src={BASE_URL + item.afterImage}
                      alt="Après"
                      className="w-full h-40 object-cover"
                    />
                  </div>
                  <div className="p-4">
                    <div className="flex items-center justify-between mb-2">
                      <span className="bg-orange-100 text-orange-600 px-3 py-1 rounded-full text-sm font-semibold">
                        {item.service}
                      </span>
                    </div>
                    <h3 className="font-bold text-gray-800 mb-2">{item.title}</h3>
                    {item.location && (
                      <p className="text-gray-600 text-sm mb-1">📍 {item.location}</p>
                    )}
                    {item.price && (
                      <p className="text-gray-600 text-sm mb-1">💰 {item.price}</p>
                    )}
                    <button
                      onClick={() => handleDelete(item._id)}
                      className="mt-3 w-full bg-red-500 text-white py-2 rounded-lg hover:bg-red-600 transition-colors font-semibold"
                    >
                      🗑️ Supprimer
                    </button>
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default AdminDashboard;